'use client';

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useWalkthrough } from '@/contexts/walkthrough-context';
import {
  WalkthroughNavigation,
  WalkthroughPanel,
  WalkthroughStepBody,
} from './walkthrough-panel';

const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1];
const TARGET_RESOLVE_POLL_INTERVAL = 120;
const TARGET_RESOLVE_MAX_WAIT = 1800;
const MISSING_TARGET_TIMEOUT = TARGET_RESOLVE_MAX_WAIT;
const TARGET_SCROLL_VIEWPORT_MARGIN = 72;
const SPOTLIGHT_FRAME_OUTSET = 1;
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

type TargetMetrics = {
  selector: string | null;
  rect: DOMRect | null;
  borderRadius: number;
};

function hasRectChanged(previous: DOMRect | null, next: DOMRect) {
  if (!previous) return true;
  return (
    previous.left !== next.left ||
    previous.top !== next.top ||
    previous.width !== next.width ||
    previous.height !== next.height
  );
}

export function useTargetMetrics(selector: string | null): TargetMetrics {
  const [metrics, setMetrics] = useState<TargetMetrics>({
    selector: null,
    rect: null,
    borderRadius: 12,
  });

  useLayoutEffect(() => {
    if (!selector) return;

    let isDisposed = false;
    let attached: Element | null = null;
    let rafHandle: number | null = null;
    let resolvePollInterval: number | null = null;
    let resolvePollTimeout: number | null = null;

    const setDisconnectedState = () => {
      setMetrics((previous) =>
        previous.selector === selector && previous.rect === null
          ? previous
          : {
              selector,
              rect: null,
              borderRadius: 12,
            }
      );
    };

    const stopRaf = () => {
      if (rafHandle !== null) {
        window.cancelAnimationFrame(rafHandle);
        rafHandle = null;
      }
    };

    const stopResolvePolling = () => {
      if (resolvePollInterval !== null) {
        window.clearInterval(resolvePollInterval);
        resolvePollInterval = null;
      }
      if (resolvePollTimeout !== null) {
        window.clearTimeout(resolvePollTimeout);
        resolvePollTimeout = null;
      }
    };

    // Single rAF loop reads the live bounding rect each frame. Captures
    // scroll, resize, layout reflows, AND CSS-transform animations (which
    // ResizeObserver misses) without firing global resize events. Idle cost
    // is one getBoundingClientRect + getComputedStyle per frame on one
    // element while a step is active.
    const tickFrame = () => {
      if (isDisposed) return;
      if (!attached || !attached.isConnected) {
        attached = null;
        setDisconnectedState();
        startResolvePolling();
        return;
      }

      const nextRect = attached.getBoundingClientRect();
      const nextRadius =
        parseFloat(getComputedStyle(attached).borderRadius) || 12;

      setMetrics((previous) => {
        const rectChanged = hasRectChanged(previous.rect, nextRect);
        const radiusChanged = previous.borderRadius !== nextRadius;
        if (!rectChanged && !radiusChanged) {
          return previous;
        }
        return {
          selector,
          rect: nextRect,
          borderRadius: nextRadius,
        };
      });

      rafHandle = window.requestAnimationFrame(tickFrame);
    };

    const attachToElement = (element: Element) => {
      stopRaf();
      attached = element;
      rafHandle = window.requestAnimationFrame(tickFrame);
    };

    const startResolvePolling = () => {
      if (resolvePollInterval !== null) return;

      resolvePollInterval = window.setInterval(() => {
        if (resolveTarget()) {
          stopResolvePolling();
        }
      }, TARGET_RESOLVE_POLL_INTERVAL);

      resolvePollTimeout = window.setTimeout(() => {
        stopResolvePolling();
      }, TARGET_RESOLVE_MAX_WAIT);
    };

    const resolveTarget = () => {
      if (isDisposed) return false;

      const element = document.querySelector(selector);
      if (!element) {
        setDisconnectedState();
        return false;
      }

      stopResolvePolling();
      attachToElement(element);
      return true;
    };

    if (!resolveTarget()) {
      startResolvePolling();
    }

    return () => {
      isDisposed = true;
      stopRaf();
      stopResolvePolling();
      attached = null;
    };
  }, [selector]);

  if (!selector) {
    return {
      selector: null,
      rect: null,
      borderRadius: 12,
    };
  }

  if (metrics.selector !== selector) {
    return {
      selector,
      rect: metrics.rect,
      borderRadius: metrics.borderRadius,
    };
  }

  return metrics;
}

function isEditableElement(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return !!target.closest(
    'input, textarea, select, [contenteditable="true"], [role="textbox"]'
  );
}

function getFocusableElements(container: HTMLElement | null) {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(
    (element) =>
      !element.hasAttribute('disabled') &&
      element.getAttribute('aria-hidden') !== 'true'
  );
}

export function WalkthroughOverlay() {
  const { currentStep, currentStepIndex, totalSteps, next, previous, skip } =
    useWalkthrough();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const inlineTitleId = useId();
  const inlineDescriptionId = useId();
  const spotlightMaskId = `walkthrough-spotlight-mask-${inlineTitleId.replace(/:/g, '')}`;
  const t = useTranslations('WalkthroughCommon');

  const { rect: targetRect, borderRadius } = useTargetMetrics(
    currentStep?.targetSelector ?? null
  );
  const inlineCardInset = 16;
  const inlineWidth =
    currentStep?.panelWidth && targetRect
      ? Math.min(
          currentStep.panelWidth,
          Math.max(targetRect.width - inlineCardInset * 2, 0)
        )
      : (targetRect?.width ?? 0);
  const inlineStyle =
    currentStep?.inlinePlacement === 'inside' && targetRect
      ? {
          left: targetRect.left + inlineCardInset,
          top: targetRect.top + inlineCardInset,
          width: inlineWidth,
        }
      : targetRect
        ? {
            left: targetRect.left,
            top: targetRect.bottom + 4,
            width: inlineWidth,
          }
        : undefined;

  // Auto-skip if target element never resolves. Driven by the metrics hook's
  // `targetRect` truth signal — when it becomes non-null mid-timer, the
  // effect re-runs and the cleanup clears the timer. No parallel race with
  // `useTargetMetrics`'s own resolve polling.
  useEffect(() => {
    if (!currentStep) return;
    if (targetRect) return;

    const timer = setTimeout(() => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `[Walkthrough] Target not found: ${currentStep.targetSelector}, skipping step`
        );
      }
      if (currentStepIndex >= totalSteps - 1) {
        skip();
      } else {
        next();
      }
    }, MISSING_TARGET_TIMEOUT);

    return () => clearTimeout(timer);
  }, [currentStep, currentStepIndex, next, skip, totalSteps, targetRect]);

  useEffect(() => {
    if (!currentStep) return;
    if (
      !restoreFocusRef.current &&
      document.activeElement instanceof HTMLElement
    ) {
      restoreFocusRef.current = document.activeElement;
    }

    const frame = window.requestAnimationFrame(() => {
      panelRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [currentStep]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const panelElement = panelRef.current;

      if (e.key === 'Escape') {
        e.preventDefault();
        skip();
        return;
      }

      if (e.key === 'ArrowRight') {
        if (isEditableElement(e.target)) return;
        e.preventDefault();
        next();
        return;
      }

      if (e.key === 'ArrowLeft') {
        if (currentStepIndex === 0) return;
        if (isEditableElement(e.target)) return;
        e.preventDefault();
        previous();
        return;
      }

      if (!panelElement) return;

      if (e.key === 'Tab') {
        const focusable = getFocusableElements(panelElement);
        if (focusable.length === 0) {
          e.preventDefault();
          panelElement.focus();
          return;
        }

        const firstFocusable = focusable[0];
        const lastFocusable = focusable[focusable.length - 1];
        const activeElement =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;

        if (e.shiftKey) {
          if (
            activeElement === panelElement ||
            activeElement === firstFocusable
          ) {
            e.preventDefault();
            lastFocusable?.focus();
          }
          return;
        }

        if (activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
          return;
        }

        if (activeElement === panelElement) {
          e.preventDefault();
          firstFocusable?.focus();
        }
        return;
      }

      const isPanelFocused = document.activeElement === panelElement;
      if (
        !isEditableElement(e.target) &&
        isPanelFocused &&
        (e.key === 'Enter' || e.key === ' ')
      ) {
        e.preventDefault();
        next();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStepIndex, next, previous, skip]);

  useEffect(() => {
    return () => {
      restoreFocusRef.current?.focus();
      restoreFocusRef.current = null;
    };
  }, []);

  const handleBackdropClick = useCallback(() => {
    skip();
  }, [skip]);

  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (!currentStep?.targetSelector) return;

    let scrollPollInterval: number | null = null;
    let scrollPollTimeout: number | null = null;

    const stopScrollPolling = () => {
      if (scrollPollInterval !== null) {
        window.clearInterval(scrollPollInterval);
        scrollPollInterval = null;
      }
      if (scrollPollTimeout !== null) {
        window.clearTimeout(scrollPollTimeout);
        scrollPollTimeout = null;
      }
    };

    const ensureTargetInView = () => {
      const element = document.querySelector(currentStep.targetSelector);
      if (!element) return false;

      const rect = element.getBoundingClientRect();
      const isOutsideViewport =
        rect.top < TARGET_SCROLL_VIEWPORT_MARGIN ||
        rect.bottom > window.innerHeight - TARGET_SCROLL_VIEWPORT_MARGIN;

      if (isOutsideViewport) {
        element.scrollIntoView({
          block: 'center',
          inline: 'nearest',
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
      }

      return true;
    };

    if (!ensureTargetInView()) {
      scrollPollInterval = window.setInterval(() => {
        if (ensureTargetInView()) {
          stopScrollPolling();
        }
      }, TARGET_RESOLVE_POLL_INTERVAL);

      scrollPollTimeout = window.setTimeout(() => {
        stopScrollPolling();
      }, TARGET_RESOLVE_MAX_WAIT);
    }

    return () => stopScrollPolling();
  }, [currentStep?.id, currentStep?.targetSelector, prefersReducedMotion]);

  // While the step is active, optionally dispatch synthetic hover events to
  // the target so tooltip / dropdown / expand-on-hover handlers fire for the
  // duration of the explanation. Also sets a data attribute so components
  // can mirror their hover styles via CSS if they choose.
  useEffect(() => {
    if (!currentStep?.previewHover) return;
    if (!currentStep.targetSelector) return;

    let attached: Element | null = null;
    let pollInterval: number | null = null;
    let pollTimeout: number | null = null;

    const dispatchHoverOnElement = (el: Element, kind: 'enter' | 'leave') => {
      const rect = el.getBoundingClientRect();
      const clientX = rect.left + rect.width / 2;
      const clientY = rect.top + rect.height / 2;
      const baseInit = {
        bubbles: true,
        clientX,
        clientY,
        view: window,
      } as const;
      const enterEvents = kind === 'enter';
      el.dispatchEvent(
        new PointerEvent(enterEvents ? 'pointerover' : 'pointerout', {
          ...baseInit,
          pointerType: 'mouse',
        })
      );
      el.dispatchEvent(
        new MouseEvent(enterEvents ? 'mouseover' : 'mouseout', baseInit)
      );
      el.dispatchEvent(
        new PointerEvent(enterEvents ? 'pointerenter' : 'pointerleave', {
          ...baseInit,
          bubbles: false,
          pointerType: 'mouse',
        })
      );
      el.dispatchEvent(
        new MouseEvent(enterEvents ? 'mouseenter' : 'mouseleave', {
          ...baseInit,
          bubbles: false,
        })
      );
    };

    const dispatchHover = (target: Element, kind: 'enter' | 'leave') => {
      // Fire on the wrapper AND on every inner button — onMouseEnter handlers
      // are typically attached to the specific button (e.g. PlusActionsCombobox
      // and the voice-call hint), not the wrapping div the spotlight targets.
      dispatchHoverOnElement(target, kind);
      const interactives = target.querySelectorAll(
        'button, [role="button"], [role="combobox"], [role="menuitem"]'
      );
      interactives.forEach((el) => dispatchHoverOnElement(el, kind));
    };

    const tryActivate = () => {
      const found = currentStep.targetSelector
        ? document.querySelector(currentStep.targetSelector)
        : null;
      if (!found) return false;
      attached = found;
      attached.setAttribute('data-walkthrough-spotlight', '');
      dispatchHover(attached, 'enter');
      return true;
    };

    if (!tryActivate()) {
      pollInterval = window.setInterval(() => {
        if (tryActivate()) {
          if (pollInterval !== null) {
            window.clearInterval(pollInterval);
            pollInterval = null;
          }
          if (pollTimeout !== null) {
            window.clearTimeout(pollTimeout);
            pollTimeout = null;
          }
        }
      }, 150);
      pollTimeout = window.setTimeout(() => {
        if (pollInterval !== null) {
          window.clearInterval(pollInterval);
          pollInterval = null;
        }
      }, 3000);
    }

    return () => {
      if (pollInterval !== null) window.clearInterval(pollInterval);
      if (pollTimeout !== null) window.clearTimeout(pollTimeout);
      if (attached) {
        attached.removeAttribute('data-walkthrough-spotlight');
        dispatchHover(attached, 'leave');
      }
    };
  }, [currentStep?.id, currentStep?.previewHover, currentStep?.targetSelector]);

  if (!currentStep) return null;

  if (!targetRect) {
    return (
      <motion.div
        className="pointer-events-none fixed inset-0 z-[60]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      />
    );
  }

  const effectiveRadius = currentStep.noRadius ? 0 : borderRadius;
  const spotlightPaddingX =
    currentStep.spotlightPaddingX ?? currentStep.spotlightPadding ?? 0;
  const spotlightPaddingY =
    currentStep.spotlightPaddingY ?? currentStep.spotlightPadding ?? 0;
  const spotlightCutoutRadius = Math.min(
    effectiveRadius + Math.max(spotlightPaddingX, spotlightPaddingY),
    (targetRect.height + spotlightPaddingY * 2) / 2
  );

  const cutout = {
    x: targetRect.left - spotlightPaddingX,
    y: targetRect.top - spotlightPaddingY,
    // Clamp: a hidden/collapsed target (e.g. covered by a dialog) can yield
    // negative dimensions, which are invalid <rect> attributes and spam
    // console errors on every tracked frame.
    width: Math.max(0, targetRect.width + spotlightPaddingX * 2),
    height: Math.max(0, targetRect.height + spotlightPaddingY * 2),
  };
  const spotlightFrame = {
    left: cutout.x - SPOTLIGHT_FRAME_OUTSET,
    top: cutout.y - SPOTLIGHT_FRAME_OUTSET,
    width: cutout.width + SPOTLIGHT_FRAME_OUTSET * 2,
    height: cutout.height + SPOTLIGHT_FRAME_OUTSET * 2,
  };
  const spotlightRadius = Math.min(
    spotlightCutoutRadius + SPOTLIGHT_FRAME_OUTSET,
    spotlightFrame.height / 2
  );

  const easeTransition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: 0.22, ease: easeOut };

  const trailTransition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: 0.28, ease: easeOut };

  return (
    <motion.div
      className="fixed inset-0 z-[60]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={
        prefersReducedMotion
          ? { duration: 0 }
          : { duration: 0.3, ease: easeOut }
      }
    >
      {/* SVG mask overlay */}
      <svg
        className="pointer-events-auto absolute inset-0 h-full w-full"
        style={{ width: '100vw', height: '100vh' }}
        onClick={handleBackdropClick}
      >
        <defs>
          <mask id={spotlightMaskId}>
            <rect width="100%" height="100%" fill="white" />
            {/* Cutout is bound to attributes directly (no Framer transition).
                The rAF tracker pushes a new rect into state every frame while
                the target animates; using `animate` here would restart a 220ms
                tween on every frame, lagging the cutout permanently behind a
                fast-growing target and hiding the grown portion under the
                dark mask. Direct attribute bindings = zero-lag tracking. */}
            <rect
              fill="black"
              x={cutout.x}
              y={cutout.y}
              width={cutout.width}
              height={cutout.height}
              rx={spotlightCutoutRadius}
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.55)"
          mask={`url(#${spotlightMaskId})`}
        />
      </svg>

      {/* Edge glow — position/size bound directly via `style` to track the
          target without Framer's tween lag. `animate` is used only for the
          one-shot opacity fade-in. */}
      <motion.div
        className="pointer-events-none absolute"
        style={{
          left: spotlightFrame.left,
          top: spotlightFrame.top,
          width: spotlightFrame.width,
          height: spotlightFrame.height,
          borderRadius: spotlightRadius,
          boxShadow:
            currentStep.pulseIntensity === 'attention'
              ? [
                  `inset 0 0 0 1px hsl(var(--primary) / 0.5)`,
                  `0 0 0 1px hsl(var(--primary) / 0.32)`,
                  `0 0 16px 2px hsl(var(--primary) / 0.22)`,
                  `0 0 28px 4px hsl(var(--primary) / 0.12)`,
                ].join(', ')
              : [
                  // Crisp frame that hugs the target
                  `inset 0 0 0 1px hsl(var(--primary) / 0.3)`,
                  // Tight outer ring
                  `0 0 0 1px hsl(var(--primary) / 0.18)`,
                  // Controlled glow for separation from the overlay
                  `0 0 10px 1px hsl(var(--primary) / 0.14)`,
                  `0 0 18px 2px hsl(var(--primary) / 0.08)`,
                ].join(', '),
          border:
            currentStep.pulseIntensity === 'attention'
              ? '1px solid hsl(var(--primary) / 0.45)'
              : '1px solid hsl(var(--primary) / 0.28)',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={easeTransition}
      />

      {/* Accent ring — position/size bound via `style` to follow the target
          in lockstep. `animate` carries only the opacity pulse loop. */}
      <motion.div
        className="pointer-events-none absolute"
        style={{
          left: spotlightFrame.left,
          top: spotlightFrame.top,
          width: spotlightFrame.width,
          height: spotlightFrame.height,
          borderRadius: spotlightRadius,
          boxShadow:
            currentStep.pulseIntensity === 'attention'
              ? [
                  `0 0 0 2px hsl(var(--primary) / 0.42)`,
                  `0 0 18px 4px hsl(var(--primary) / 0.24)`,
                  `0 0 32px 8px hsl(var(--primary) / 0.12)`,
                ].join(', ')
              : [
                  `0 0 0 1px hsl(var(--primary) / 0.22)`,
                  `0 0 12px 2px hsl(var(--primary) / 0.08)`,
                ].join(', '),
          border:
            currentStep.pulseIntensity === 'attention'
              ? '1px solid hsl(var(--primary) / 0.32)'
              : '1px solid hsl(var(--primary) / 0.14)',
        }}
        initial={{ opacity: 0 }}
        animate={{
          opacity: prefersReducedMotion
            ? currentStep.pulseIntensity === 'attention'
              ? 0.85
              : 0.5
            : currentStep.pulseIntensity === 'attention'
              ? [0.95, 0.45, 0.95]
              : [0.52, 0.2, 0.52],
        }}
        transition={
          prefersReducedMotion
            ? easeTransition
            : {
                ...trailTransition,
                duration:
                  currentStep.pulseIntensity === 'attention' ? 1.4 : 2.2,
                repeat: Infinity,
                repeatType: 'mirror',
              }
        }
      />

      {/* Panel — inline (docked to element) or floating */}
      <div className="pointer-events-none absolute inset-0">
        <AnimatePresence mode="wait">
          {currentStep.panelMode ===
          'embedded' ? null : currentStep.panelMode === 'inline' ? (
            <motion.div
              key={currentStep.id}
              className="pointer-events-auto absolute"
              style={inlineStyle}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={easeTransition}
            >
              <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={inlineTitleId}
                aria-describedby={inlineDescriptionId}
                aria-keyshortcuts="ArrowLeft ArrowRight Escape"
                tabIndex={-1}
                className="border-primary/20 bg-popover text-popover-foreground focus-visible:ring-primary/30 rounded-2xl border p-4 shadow-2xl focus:outline-none focus-visible:ring-2"
              >
                <WalkthroughStepBody
                  step={currentStep}
                  titleId={inlineTitleId}
                  descriptionId={inlineDescriptionId}
                />
                <p className="sr-only">{t('instructions')}</p>
                <WalkthroughNavigation
                  currentIndex={currentStepIndex}
                  totalSteps={totalSteps}
                  onNext={next}
                  onPrevious={previous}
                  onSkip={skip}
                />
              </div>
            </motion.div>
          ) : (
            <WalkthroughPanel
              key={currentStep.id}
              ref={panelRef}
              step={currentStep}
              targetRect={targetRect}
              currentIndex={currentStepIndex}
              totalSteps={totalSteps}
              onNext={next}
              onPrevious={previous}
              onSkip={skip}
            />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
